/**
 * ThemeToggle — per-device light / dark / auto switch.
 *
 * A segmented control wired to the `useThemePref` store.  Picking Lys or
 * Mørk forces the theme on this device; Auto hands control back to
 * DuskTheme (admin override + Met.no sun times).  The choice is persisted
 * to localStorage and applied app-wide by DuskTheme, which reads `pref`.
 *
 * #152: kontrollen er SegmentedTabs-primitiven — spor `bg-tab-bg`, aktivt
 * valg fylt `bg-solid`. Den er fortsatt en ARIA radiogroup, og testid-ene
 * `theme-pref-*` sitter på de samme knappene.
 *
 * #159 (steg 9d, Stell_Meg): skissens etikett «Tema på denne enheten»,
 * ikoner og tekster («Etter solen», «Lyst», «Mørkt»), og et hint under som
 * følger valget. Husstandstemaet får ikke noe valg her (D14); det virker
 * fortsatt under «Etter solen», i DuskTheme sin rekkefølge.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/primitives/Icon';
import { SegmentedTabs } from '../../components/primitives/SegmentedTabs';
import { ADMIN_LABEL } from '../admin/adminStyles';
import { useThemePref, type ThemePref } from '../dashboard/useThemePref';

const OPTIONS: readonly { value: ThemePref; i18nKey: string; icon: ReactNode }[] = [
  { value: 'auto', i18nKey: 'theme.option.auto', icon: <Icon.ThemeAuto size={16} /> },
  { value: 'light', i18nKey: 'theme.option.light', icon: <Icon.ThemeLight size={16} /> },
  { value: 'dark', i18nKey: 'theme.option.dark', icon: <Icon.ThemeDark size={16} /> },
] as const;

export function ThemeToggle() {
  const { t } = useTranslation();
  const pref = useThemePref((s) => s.pref);
  const setPref = useThemePref((s) => s.setPref);

  return (
    <section data-testid="theme-toggle" aria-labelledby="meg-theme" className="flex flex-col gap-2">
      <h2 id="meg-theme" className={ADMIN_LABEL}>
        {t('theme.device_title')}
      </h2>
      <SegmentedTabs
        label={t('theme.device_title')}
        dense
        className="w-full md:max-w-md"
        value={pref}
        onChange={setPref}
        options={OPTIONS.map((o) => ({
          value: o.value,
          label: t(o.i18nKey),
          icon: o.icon,
          testId: `theme-pref-${o.value}`,
        }))}
      />
      <p data-testid="theme-hint" className="m-0 text-caption text-ink-2">
        {pref === 'auto' ? t('theme.hint_auto') : t('theme.device_hint')}
      </p>
    </section>
  );
}
